<script setup lang="ts">
import { EVENT_TYPE, SHARE_SOCIAL_LINK } from "~/constants";
import useEventStore from "~/stores/event";

export interface EventInfoShareProps {
  canShareOnSchedule?: boolean;
  event: IModelEvent;
}
const props = defineProps<EventInfoShareProps>();
const { t } = useI18n();
const eventStore = useEventStore();
const shareAtSpecificTime = ref(false);

const isClip = computed(() => props.event.eventType === EVENT_TYPE.USER_CLIP);
const currentTime = computed(() => formatTime(eventStore.playerState?.currentTime || 0));
const pauseVideo = () => eventStore.player?.pause();
const playVideo = () => eventStore.player?.play();

const shareUrl = computed(() => {
  const baseUrl = window.location.href;
  const url = new URL(baseUrl);

  if (url.searchParams.has("time"))
    url.searchParams.delete("time");

  if (shareAtSpecificTime.value) {
    url.searchParams.set("time", eventStore.playerState?.currentTime.toFixed(2) || "0");
    return url.toString();
  }

  return url.toString();
});

const shareData = computed(() => ({
  url: shareUrl.value,
  title: props.event.title,
  eventId: props.event.eventId || props.event.id,
  clipId: isClip.value ? props.event.id : undefined,
  isClip: isClip.value,
}));

const { shareProviderUrl, trackShare } = useSharing(shareData);
</script>

<template>
  <BaseModal root-class="max-w-[430px]" @close="playVideo()">
    <template #trigger="{ openModal }">
      <BaseButton class="text-link !no-underline" type="secondary" name="share" link @click="pauseVideo(); openModal()">
        <div class="flex items-center gap-2 hover:bg-[var(--base-category-chip-bgColor)] px-2 py-1 rounded-[var(--base-border-radius)]">
          <IconShare class="inline-block" fill="currentColor" :width="16" :height="16" />
          <span class="text-[var(--base-secondary-text-color)]">{{ t('labels.share') }}</span>
        </div>
      </BaseButton>
    </template>
    <template #before-title>
      <div class="flex justify-center">
        <h3 class="text-heading-3 text-main" v-text="t('pages.events.modal_share_title')" />
      </div>
    </template>
    <template #actions>
      <div>
        <div class="my-6 flex items-center justify-center gap-7">
          <div class="flex flex-col justify-center items-center">
            <BaseButton
              tag="a"
              type="icon"
              name="clipboard-copy-facebook"
              :href="shareProviderUrl(SHARE_SOCIAL_LINK.FACEBOOK)"
              target="_blank"
              @click="trackShare(SHARE_SOCIAL_LINK.FACEBOOK)"
            >
              <IconShareFacebook />
            </BaseButton>
            <span class="text-[11px] text-secondary mt-2">Facebook</span>
          </div>

          <div class="flex flex-col justify-center items-center">
            <BaseButton
              tag="a"
              type="icon"
              name="clipboard-copy-x"
              :href="shareProviderUrl(SHARE_SOCIAL_LINK.X)"
              target="_blank"
              @click="trackShare(SHARE_SOCIAL_LINK.X)"
            >
              <IconShareX />
            </BaseButton>
            <span class="text-[11px] text-secondary mt-2">X</span>
          </div>

          <div class="flex flex-col justify-center items-center">
            <BaseButton
              tag="a"
              type="icon"
              name="clipboard-copy-whatsapp"
              :href="shareProviderUrl(SHARE_SOCIAL_LINK.WHATSAPP)"
              target="_blank"
              @click="trackShare(SHARE_SOCIAL_LINK.WHATSAPP)"
            >
              <IconShareWhatsApp />
            </BaseButton>
            <span class="text-[11px] text-secondary mt-2">WhatsApp</span>
          </div>
        </div>
        <BaseInput
          :model-value="shareUrl"
          class="font-medium"
          name="share-url"
          has-clipboard-copy
          outline
          readonly
        />
        <BaseCheckbox
          v-if="props.canShareOnSchedule"
          v-model="shareAtSpecificTime"
          name="share-url-time"
          :label="t('pages.events.modal_share_with_time', { time: currentTime })"
          class="text-secondary mt-4"
        />
      </div>
    </template>
  </BaseModal>
</template>
